import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ToggleButton,
  ToggleButtonGroup,
  Button,
  TextField,
} from '@mui/material'
import { toast } from 'react-toastify'
import { BACKEND_URI } from '../config'


function ManageUsers({ user }) {
  const navigate = useNavigate()
  const [userInformation, setuserInformation] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredUsers, setFilteredUsers] = useState(userInformation)
  const [showApproved, setShowApproved] = useState(true) // Toggle state for approved/unapproved
  const [areYouSureDeleteButtonVisible, setareYouSureDeleteButtonVisible] =
    useState(false)
  const [deleteButtonShown, setDeleteButtonShown] = useState()
  const [addingNotes, setAddingNotes] = useState(false)
  const [tempNotes, setTempNotes] = useState({})

  const handleNavigate = path => {
    navigate(path)
  }

  useEffect(() => {
    const fetchUserInformation = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(
          `${BACKEND_URI}/api/userInformation`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

        const data = await response.json()
        setuserInformation(data)
        filterUsers('', showApproved, data)
      } catch (error) {
        console.error('Error fetching user information:', error)
      }
    }

    if (user.admin) {
      fetchUserInformation()
    } else {
      handleNavigate('/')
    }
  }, [])

  // Handle search input change
  const handleSearch = e => {
    const term = e.target.value.toLowerCase()
    setSearchTerm(term)
    filterUsers(term, showApproved)
  }

  const handleSaveNotes = async userId => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(
        `${BACKEND_URI}/api/update-user-notes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id: userId, notes: tempNotes[userId] }),
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'An error occurred')
      }

      // Update the notes in filteredUsers state
      setFilteredUsers(prevUsers =>
        prevUsers.map(user =>
          user._id === userId ? { ...user, notes: tempNotes[userId] } : user
        )
      )
    } catch (error) {
      console.error('Error fetching user information:', error)
    }
    setAddingNotes(false)
  }

  function filterUsers(term, approvedStatus, user_information) {
    let userInformationTemp = ''
    if (user_information) {
      userInformationTemp = user_information
    } else {
      userInformationTemp = userInformation
    }

    if (term === '') {
      const filtered = userInformationTemp.filter(userTemp => {
        return userTemp.approved === approvedStatus && userTemp._id !== user.id
      })
      setFilteredUsers(filtered)
    } else {
      const filtered = userInformationTemp.filter(userTemp => {
        const matchesSearch =
          userTemp.username.toLowerCase().includes(term) ||
          userTemp.firstname.toLowerCase().includes(term) ||
          userTemp.lastname.toLowerCase().includes(term) ||
          userTemp.email.toLowerCase().includes(term) ||
          userTemp.phonenumber.includes(term)

        return (
          matchesSearch &&
          userTemp.approved === approvedStatus &&
          userTemp._id !== user.id
        )
      })

      setFilteredUsers(filtered)
    }
  }

  const handleToggle = (event, newStatus) => {
    if (newStatus !== null) {
      setShowApproved(newStatus === 'approved')
      filterUsers(searchTerm, newStatus === 'approved')
    }
  }

  const handleChangeApproval = async (user_id, newValue, username) => {
    if (user_id && newValue) {
      try {
        let newValueApproved = true
        if (newValue !== 'approved') {
          newValueApproved = false
        }
        const token = localStorage.getItem('token') // JWT for authentication
        const response = await fetch(
          `${BACKEND_URI}/api/changeApproval`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ user_id, newValueApproved }), // Send custom invite code
          }
        )

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || 'Failed to update approval status')
        }

        await fetch(`${BACKEND_URI}/api/remove_pending_approval`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ username }), // Send custom invite code
        })
        toast.success('Approval status updated')
      } catch (err) {
        console.error('Error updating approval status:', err)
        toast.error(err.message || 'Error updating approval status.')
      }

      const updatedUsers = userInformation.map(userinfo =>
        userinfo._id === user_id
          ? { ...userinfo, approved: !userinfo.approved }
          : userinfo
      )

      // Revert the optimistic UI update in case of error
      setuserInformation(updatedUsers)

      filterUsers(searchTerm, showApproved, updatedUsers)
    }
  }

  const handleDeleteUser = async user_id => {
    try {
      const token = localStorage.getItem('token') // JWT for authentication
      const response = await fetch(`${BACKEND_URI}/api/delete-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userone: user_id }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete user')
      }

      const updatedUsers = userInformation.filter(u => u._id !== user_id)

      // Update the state with the new codes
      setuserInformation(updatedUsers)
      filterUsers(searchTerm, showApproved, updatedUsers)
      setareYouSureDeleteButtonVisible(false)

      toast.success('Successfully deleted user')
    } catch (err) {
      console.error('Error deleting user:', err)
      toast.error(err.message || 'Error deleting user.')
    }
  }

  return (
    <div
      style={{
        padding: '20px',
        border: '1px solid #ddd',
        borderRadius: '10px',
        backgroundColor: '#f9f9f9',
      }}
    >
      <h2>User Information</h2>

      <input
        type='text'
        placeholder='Search by username, email, or phone number'
        value={searchTerm}
        onChange={handleSearch}
        style={{
          padding: '10px',
          width: '100%',
          borderRadius: '5px',
          border: '1px solid #ccc',
          marginBottom: '20px',
        }}
      />

      <ToggleButtonGroup
        color='primary'
        value={showApproved ? 'approved' : 'unapproved'}
        exclusive
        onChange={handleToggle}
        aria-label='approval status'
        sx={{ marginBottom: 3 }}
      >
        <ToggleButton value='approved' aria-label='approved' color='success'>
          Approved Users
        </ToggleButton>
        <ToggleButton value='unapproved' aria-label='unapproved' color='error'>
          Unapproved Users
        </ToggleButton>
      </ToggleButtonGroup>

      {filteredUsers.length > 0 ? (
        filteredUsers.map((user, idx) => (
          <div
            key={user._id}
            style={{
              marginBottom: '20px',
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '5px',
            }}
          >
            <p>
              <strong>First Name:</strong> {user.firstname}
            </p>
            <p>
              <strong>Last Name:</strong> {user.lastname}
            </p>
            <p>
              <strong>Email:</strong> {user.email}
            </p>
            <p>
              <strong>Phone Number:</strong> {user.phonenumber}{' '}
            </p>
            <p>
              <strong>Verified:</strong> {user.verified? "TRUE": "FALSE"}
            </p>
            <p>
              <strong>Funds:</strong> ${user.funds || 0}
            </p>
            <p>
              <strong>Username:</strong> {user.username}
            </p>
            <p>
              <strong>Admin:</strong> {user.admin ? 'Yes' : 'No'}
            </p>
            <p>
              <strong>Created At:</strong>{' '}
              {new Date(user.createdAt).toLocaleString()}
            </p>
            {addingNotes === user._id ? (
              <>
                <TextField
                  label='Modify Notes'
                  value={tempNotes[user._id] || user.notes}
                  onChange={e =>
                    setTempNotes(prevNotes => ({
                      ...prevNotes,
                      [user._id]: e.target.value,
                    }))
                  }
                  fullWidth
                  style={{ marginBottom: '10px' }}
                />
                <Button
                  variant='contained'
                  style={{ backgroundColor: '#51a4ac', marginRight: '10px' }}
                  onClick={() => handleSaveNotes(user._id)}
                >
                  Submit
                </Button>
                <Button
                  variant='contained'
                  style={{ backgroundColor: '#c53b3a' }}
                  onClick={() => setAddingNotes(null)}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <p>
                  <strong>Notes:</strong> {user.notes}
                </p>
                <Button
                  variant='contained'
                  style={{ backgroundColor: '#51a4ac', marginBottom: '10px' }}
                  onClick={() => setAddingNotes(user._id)}
                >
                  Add Notes
                </Button>
              </>
            )}
            <div style={{ padding: '10px' }} />

            <ToggleButtonGroup
              color='primary'
              value={user.approved ? 'approved' : 'unapproved'}
              exclusive
              onChange={(e, newValue) =>
                handleChangeApproval(user._id, newValue, user.username)
              }
              aria-label='approval status'
              sx={{ marginBottom: 3 }}
            >
              <ToggleButton
                value='approved'
                aria-label='approved'
                color='success'
              >
                Approved
              </ToggleButton>
              <ToggleButton
                value='unapproved'
                aria-label='unapproved'
                color='error'
              >
                Unapproved
              </ToggleButton>
            </ToggleButtonGroup>
            <div />
            <button
              onClick={e => {
                setareYouSureDeleteButtonVisible(true)
                setDeleteButtonShown(idx)
              }}
              style={
                areYouSureDeleteButtonVisible && deleteButtonShown === idx
                  ? styles.buttonDisabled
                  : styles.cancelButton
              }
            >
              Delete
            </button>
            <div />
            {areYouSureDeleteButtonVisible && deleteButtonShown === idx && (
              <div style={{ marginTop: '.5rem', marginBottom: '.5rem' }}>
                <label>Are you sure you want to delete this user?</label>
                <div />
                <button
                  style={styles.saveButton}
                  onClick={e => handleDeleteUser(user._id)}
                >
                  Yes
                </button>
                <button
                  style={styles.cancelButton}
                  onClick={e => setareYouSureDeleteButtonVisible(false)}
                >
                  No
                </button>
              </div>
            )}
          </div>
        ))
      ) : (
        <p>No users found.</p>
      )}
    </div>
  )
}

export default ManageUsers

const styles = {
  buttonDisabled: {
    backgroundColor: 'grey',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  saveButton: {
    marginTop: ".5rem",
    padding: '0.5rem 1rem',
    backgroundColor: '#51a4ac',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#c53b3a',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
}
